"use client";

import { useMemo } from "react";
import type { RemoveBgJob } from "@/types/removebg";
import { formatBytes } from "@/utils/removebg/validators";

interface RemoveBgPreviewProps {
  job: RemoveBgJob;
  originalSource?: string | null;
  onSendAnotherImage?: () => void;
}

export default function RemoveBgPreview({ job, originalSource, onSendAnotherImage }: RemoveBgPreviewProps) {
  const originalUrl = useMemo(() => originalSource ?? job.url_original ?? job.url_preview, [job.url_original, job.url_preview, originalSource]);
  const processedUrl = job.url_hd ?? job.url_preview;

  return (
    <div className="space-y-4 sm:space-y-6">
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div>
          <h2 className="text-lg font-semibold text-white sm:text-xl">Pré-visualização</h2>
          <p className="text-xs text-white/60 sm:text-sm">Compare o original com o recorte gerado automaticamente.</p>
        </div>
        {onSendAnotherImage && (
          <button
            type="button"
            onClick={onSendAnotherImage}
            className="w-full rounded-full bg-brand-primary px-4 py-2 text-sm font-semibold text-white transition hover:bg-brand-secondary focus-visible:outline-none sm:w-auto sm:px-6"
          >
            Enviar outra imagem
          </button>
        )}
      </div>

      <div className="grid gap-4 sm:gap-6 md:grid-cols-2">
        <figure className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <div className="relative aspect-square w-full">
            {originalUrl ? (
              <>
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img src={originalUrl} alt="Pré-visualização original" className="absolute inset-0 h-full w-full object-contain" loading="eager" />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/40">Sem imagem original</div>
            )}
          </div>
          <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm text-white">
            Original ({job.original.filename} • {formatBytes(job.original.size_bytes)})
          </figcaption>
        </figure>

        <figure className="group relative overflow-hidden rounded-2xl border border-white/10 bg-white/5">
          <div className="relative aspect-square w-full">
            {processedUrl ? (
              <>  
                {/* eslint-disable-next-line @next/next/no-img-element */}
                <img
                  src={processedUrl}
                  alt="Imagem com fundo removido"
                  className="absolute inset-0 h-full w-full object-contain"
                  loading="eager"
                  style={{
                    backgroundImage: 'url(/images/backgrounds/fundocriedesign.jpg)',
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat'
                  }}
                />
              </>
            ) : (
              <div className="flex h-full w-full items-center justify-center text-white/40">Sem resultado disponível</div>
            )}
          </div>
          <figcaption className="absolute bottom-0 left-0 right-0 bg-gradient-to-t from-black/70 to-transparent p-4 text-sm text-white">
            Fundo removido ({job.processing_time_ms ?? 0} ms)
          </figcaption>
        </figure>
      </div>
    </div>
  );
}
